import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm, ACTION_LABELS } from "../../lib/permissions";
import { PageHead } from "./common";

const CAPABILITIES = [
  ["View Shipping Dashboard", "sea_freight", "view"],
  ["Search Freight Rates", "sea_freight", "view"],
  ["View Shipping News", "sea_freight", "view"],
  ["View Vessel Tracking", "sea_freight", "view"],
  ["Manage Saved Routes", "sea_freight", "create"],
  ["Add Freight Quotations", "sea_freight", "create"],
  ["Record Rates & Port Status", "sea_freight", "edit"],
  ["Approve Freight Quotations", "sea_freight", "approve"],
  ["Manage Alerts", "sea_freight", "create"],
  ["Delete Routes, Rates & Quotations", "sea_freight", "delete"],
  ["Export PDF Reports", "sea_freight", "export_pdf"],
  ["Export Excel Reports", "sea_freight", "export_excel"],
  ["Manage Data Sources & API Integrations", "sea_freight_admin", "edit"],
];

export default function FreightSettings() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);

  useEffect(() => {
    api.get("/sea-freight/data-sources").then((r) => setSources(r.data.sources || []))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  return (
    <div data-testid="freight-settings-page">
      <PageHead title="Settings & Permissions"
        subtitle="What this module does, what each permission unlocks, and which integrations are live. Roles are assigned by an Admin in User Management." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-sm">
          <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">Your sea freight permissions</h2></header>
          <div className="divide-y divide-slate-100" data-testid="permissions-matrix">
            {CAPABILITIES.map(([label, mod, action]) => {
              const allowed = hasPerm(user, mod, action);
              return (
                <div key={label} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-800">{label}</div>
                    <div className="text-[11px] text-slate-400">{mod} · {ACTION_LABELS[action] || action}</div>
                  </div>
                  <Badge variant="outline" data-testid={`perm-${action}-${mod}`}
                    className={`rounded-full px-2 text-[11px] ${allowed ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {allowed ? "Allowed" : "Not allowed"}
                  </Badge>
                </div>
              );
            })}
          </div>
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            Backend enforces every one of these — a blocked action cannot be reached by calling the API directly.
            {user?.role === "admin" && <> Assign them in <Link to="/users" className="underline hover:text-slate-900">User Management</Link>.</>}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-sm">
          <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">Integration status</h2></header>
          <div className="divide-y divide-slate-100">
            {sources.map((s) => (
              <div key={s.key} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-800">{s.name}</div>
                  <div className="text-[11px] text-slate-400">{s.category}{s.env_key ? ` · needs ${s.env_key}` : " · no key required"}</div>
                </div>
                <Badge variant="outline" className={`rounded-full px-2 text-[11px] ${s.effective_status === "Active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : s.effective_status === "API key required" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {s.effective_status}
                </Badge>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            Paid providers (Freightos, Xeneta, SeaRates, MarineTraffic, Datalastic, Portcast) stay disabled until their key is added as a backend secret.
            Manage them in <Link to="/sea-freight/data-sources" className="underline hover:text-slate-900">Data Sources</Link>.
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-sm lg:col-span-2 p-4 text-sm text-slate-600 space-y-2">
          <h2 className="text-base font-semibold text-slate-900 font-head">Data accuracy rules applied in this module</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Freight rates are never generated or estimated. Each one is stored as API Verified, Forwarder Quoted, Marketplace Indicative, Manually Entered or Historical — and only the first two are treated as bookable.</li>
            <li>When no rate exists for a lane, the module says "Live rate unavailable. Request a freight quotation." instead of showing a placeholder comparison.</li>
            <li>Port congestion shows "Unknown" unless a value has actually been recorded, with its source and timestamp.</li>
            <li>Vessel positions and ETA are shown only from a licensed AIS provider or your own entry, with the data age and a "stale" marker after 6 hours.</li>
            <li>AI news analysis is always labelled as prediction, separate from the publisher's headline and source link.</li>
            <li>Rate history keeps every observation; comparisons are normalised to one container size and currency at a time.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
